import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import {
  DEFAULT_NURTURE_TEMPLATES,
  renderNurtureCopy,
  agentForStudio,
  nurtureSummary,
  segmentForRedKind,
  type NurtureSegment,
  type NurtureTouch,
  type NurtureInbound,
} from '@/domain/nurtureFlow';

/**
 * The store-touching nurture seam — the mock JustCall integration.
 *
 * `sendNurture` renders the segment's editable copy and records an outbound
 * touch (no real API this phase). `nurtureInbound` is the reply / call-back:
 * it routes to the customer's studio agent and records a NurtureInbound that
 * the OperatorQueue Alerts tab surfaces with its "why" summary.
 */
function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

export function sendNurture(
  customerId: string,
  segment: NurtureSegment,
  opts?: { at?: string; bodyOverride?: string },
): { ok: boolean; touchId?: string } {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  if (!customer) return { ok: false };

  const at = opts?.at ?? nowIso();
  const studioId = customer.primaryStudioId;
  const studio = brain.studios[studioId];
  const artist = customer.preferredArtistId ? brain.artists[customer.preferredArtistId] : undefined;

  const template =
    opts?.bodyOverride ?? brain.nurtureTemplates[segment] ?? DEFAULT_NURTURE_TEMPLATES[segment].body;
  const body = renderNurtureCopy(template, {
    name: customer.firstName,
    studio: studio?.name,
    artist: artist?.displayName,
  });

  const touch: NurtureTouch = {
    id: `nurture-touch-${customerId}-${at}`,
    customerId,
    studioId,
    segment,
    body,
    sentAt: at,
  };
  brain.upsertNurtureTouch(touch);
  eventBus.publish('nurture:sent', { customerId, studioId, segment, touchId: touch.id, at });
  return { ok: true, touchId: touch.id };
}

/**
 * Automated reds-recycle feeder. Every red lead (light = never-reached,
 * dark = said-no) that has not yet had a nurture touch for its segment gets one
 * sent. Idempotent — one touch per (customer, segment). Runs on clock moves.
 * The historic / lapsed-cadence feeder is separate (pending). Returns the count.
 */
/** Lapsed re-engagement is rate-limited per reconcile pass (a rolling list). */
const HISTORIC_CAP_PER_PASS = 10;

export function reconcileNurture(nowIso?: string): number {
  const brain = useBrainStore.getState();
  const at = nowIso ?? useSimulationStore.getState().clockIso;
  const already = new Set(
    Object.values(brain.nurtureTouches).map((t) => `${t.customerId}:${t.segment}`),
  );
  let sent = 0;

  // Reds recycle: every red lead → its segment touch.
  for (const [customerId, seq] of Object.entries(brain.sequenceState)) {
    if (seq.stage !== 'red' || !seq.redKind) continue;
    const segment = segmentForRedKind(seq.redKind);
    if (!segment) continue;
    if (already.has(`${customerId}:${segment}`)) continue;
    if (!brain.customers[customerId]) continue;
    if (sendNurture(customerId, segment, { at }).ok) {
      already.add(`${customerId}:${segment}`);
      sent += 1;
    }
  }

  // Historic / lapsed cadence: re-engage lapsed clients (6–18 months), a capped
  // rolling list per pass. dormant/do-not-contact are excluded.
  let historic = 0;
  for (const c of Object.values(brain.customers)) {
    if (historic >= HISTORIC_CAP_PER_PASS) break;
    if (c.engagementStatus !== 'lapsed' || c.doNotContact) continue;
    if (already.has(`${c.id}:historic`)) continue;
    if (sendNurture(c.id, 'historic', { at }).ok) {
      already.add(`${c.id}:historic`);
      historic += 1;
      sent += 1;
    }
  }
  return sent;
}

export function nurtureInbound(
  customerId: string,
  kind: 'reply' | 'callback',
  opts?: { at?: string; text?: string },
): { ok: boolean; inboundId?: string; agentId?: string | null } {
  const brain = useBrainStore.getState();
  const customer = brain.customers[customerId];
  if (!customer) return { ok: false };

  const at = opts?.at ?? nowIso();
  const studioId = customer.primaryStudioId;

  // The most recent touch gives the segment + the copy that went out.
  const lastTouch = Object.values(brain.nurtureTouches)
    .filter((t) => t.customerId === customerId)
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0];
  const segment: NurtureSegment = lastTouch?.segment ?? 'historic';

  const agentId = agentForStudio(studioId, { studios: brain.studios, agents: brain.agents });
  const summary = nurtureSummary({ segment, kind, text: opts?.text });

  const inbound: NurtureInbound = {
    id: `nurture-inbound-${customerId}-${at}`,
    customerId,
    studioId,
    agentId,
    segment,
    kind,
    text: opts?.text,
    summary,
    touchBody: lastTouch?.body,
    at,
  };
  brain.upsertNurtureInbound(inbound);
  eventBus.publish('nurture:inbound', {
    customerId,
    studioId,
    agentId,
    segment,
    kind,
    inboundId: inbound.id,
    at,
  });
  return { ok: true, inboundId: inbound.id, agentId };
}
